// @ts-nocheck

// If you make changes in this file, please remove the first line
// and do your best to leave this file more typechecked then it was before

import { ANNOTATION_TYPES } from 'app/constants/annotation.constants'
import { inRange } from 'app/utils/general'
import { ElementRange } from 'app/types/annotation.types'
import { TODO } from 'app/types/index.types'

const higlightLineRange = 4

export const getElementByCFI = (
  cfi?: string | null,
  element = 'g',
  container: HTMLElement | Document = document
): HTMLElement =>
  container.querySelector(`${element}[data-epubcfi='${cfi}']`) as HTMLElement

export const getIframes = (container = document) =>
  [...container.querySelectorAll('iframe')].map(
    (iframe) => iframe.contentDocument
  )

export const getIframeIds = (container = document) =>
  getIframes(container).map((iframe) => iframe?.body.id)

export const getCfiBodyId = (cfi?: string, iframes: HTMLIFrameElement[]) => {
  if (!cfi) return false
  const pageIframe = iframes.filter((iframe) =>
    cfi.includes(iframe.contentDocument.body.id)
  )[0]

  return pageIframe?.contentDocument?.body?.id || null
}

export const isInSecondIframe = (cfi: string, iframes = getIframes()) =>
  !!iframes[1] && cfi.includes(iframes[1].body.id)

export const resetMarksClassList = (container = document) => {
  const marks = [
    ...container.querySelectorAll('a[ref="epubjs-mk"].active'),
    ...container.querySelectorAll('div.group-mark.active'),
  ]

  marks.forEach((mark) => mark.classList.remove('active'))
}

export const showMark = (cfi: string) => {
  const mark = getElementByCFI(cfi, 'a')
  if (mark) mark.style.display = 'block'
}

export const hideMark = (cfi: string) => {
  const mark = getElementByCFI(cfi, 'a')
  if (mark) mark.style.display = 'none'
}

const getSpanNumberFromId = (id?: string) =>
  typeof id === 'string' && Number(id.match(/\d+/)?.[0])

export const getSpanIdFromCfi = (cfi: string) => {
  const spanIdRegex = /_idTextSpan\d+/g
  const matches = cfi.match(spanIdRegex)
  const first = getSpanNumberFromId(matches?.[0])
  const last = getSpanNumberFromId(matches?.[1]) || first

  return [first, last]
}

export const getHighlightedSpansIds = (
  annotations: TODO,
  isRemoved = false
) => {
  if (!annotations) return {}

  return Object.values(annotations).reduce((result, { cfi, type }) => {
    if (type !== ANNOTATION_TYPES.HIGHLIGHT || !cfi) return result

    const [first, last] = getSpanIdFromCfi(cfi)
    if (!first || !last) return {}

    for (let i = first; i <= last; i++) {
      result[`_idTextSpan${i}`] = !isRemoved && cfi
    }

    return result
  }, {})
}

export const getHighlightRange = (cfi: string, container: Document) => {
  const ids = getSpanIdFromCfi(cfi)

  const rangeStart = container.getElementById(`_idTextSpan${ids[0]}`)
  const rangeEnd = container.getElementById(`_idTextSpan${ids[1] || ids[0]}`)

  const range = new Range()
  if (!rangeStart || !rangeEnd) return range

  range.setStart(rangeStart, 0)
  range.setEnd(rangeEnd, 0)

  return range
}

export const changeHighlightColor = (cfi: any, color: string) => {
  const el = getElementByCFI(cfi, 'g')
  el.setAttribute('fill', color)
}

export const createSolidHiglight = (el: Element) => {
  const children = [...el.childNodes] as HTMLElement[]
  const higlightMap = {} as TODO

  // map through all children of higlight element
  // find lines start and end position
  // delete all element except first in line
  // Update first elements atributes
  children.forEach((childrenEl) => {
    const left = Math.round(Number(childrenEl.getAttribute('x')))
    const top = Math.round(Number(childrenEl.getAttribute('y')))
    const height = Math.round(Number(childrenEl.getAttribute('height')))
    const width = Math.round(Number(childrenEl.getAttribute('width')))

    if (!height || !width) {
      return childrenEl.parentNode?.removeChild(childrenEl)
    }

    const rangeLine = Object.keys(higlightMap).find((line) =>
      inRange(line, top - higlightLineRange, top + higlightLineRange)
    )

    if (!rangeLine || !higlightMap[rangeLine]) {
      // if it first element in higlight line
      higlightMap[top] = {
        top,
        start: left,
        end: left + width,
        element: childrenEl,
      }
    } else {
      // find line smallest x value / start position
      if (left < higlightMap[rangeLine].start) {
        higlightMap[rangeLine].start = left
      }

      // find line biggest x value / end position
      if (left + width > higlightMap[rangeLine].end) {
        higlightMap[rangeLine].end = left + width
      }

      // if it isn't the first element, remove it
      childrenEl.parentNode.removeChild(childrenEl)
    }
  })

  // Update value attributes for line heighlight
  Object.keys(higlightMap).forEach((key) => {
    const area = higlightMap[key]

    area.element.setAttribute('x', area.start)
    area.element.setAttribute('width', area.end - area.start)
  })
}

export const createSolidHiglightsForPage = () => {
  const higlights = [...document.querySelectorAll('g.epubjs-hl')]
  higlights.forEach((el) => createSolidHiglight(el))
}

// Get span position
const getElementPosition = (
  el: Element
): { left: number | null; right: number | null } => {
  if (!el) return { left: null, right: null }
  const style = window.getComputedStyle(el)
  const left = Number(style.getPropertyValue('left').slice(0, -2)) // slice to remove 'px' from the end
  const width = Number(style.getPropertyValue('width').slice(0, -2))

  return {
    left: Math.round(left),
    right: Math.round(left + width),
  }
}

// if there is no space on the side of the word, it's siblings may possibly be part of the same word
const isWordBoundary = (el: Element | null, position: 'start' | 'end') => {
  if (!el) return false
  const text = el.textContent
  return text?.[position === 'start' ? 0 : text.length - 1] === ' '
}

// Get nearest spans that constitute one word
const getSiblings = (
  el: Element
): { leftSibling?: Element; rightSibling?: Element } => {
  const { left, right } = getElementPosition(el)
  const nextEl = !isWordBoundary(el, 'end') ? el.nextElementSibling : null
  const prevEl = !isWordBoundary(el, 'start') ? el.previousElementSibling : null

  const { right: prevRight } = getElementPosition(prevEl)
  const { left: nextLeft } = getElementPosition(nextEl)
  return {
    leftSibling:
      inRange(left - prevRight, -30, 50) &&
      !isWordBoundary(prevEl, 'end') &&
      prevEl, // sometimes gap between spans of one word can be up to 50px
    rightSibling:
      inRange(nextLeft - right, -30, 50) &&
      !isWordBoundary(nextEl, 'start') &&
      nextEl,
  }
}

// Recursively maps over the siblings to find all the spans that consitute one word
// (some books have one word split in multiple HTML elements)
const getWholeWord = (
  el: Element,
  memo: Set<Element> = new Set()
): Set<Element> => {
  if (!el || memo.has(el) || !el.textContent?.trim()) return memo
  memo.add(el)
  const { leftSibling, rightSibling } = getSiblings(el)
  if (!leftSibling && !rightSibling) return memo

  const leftPart = getWholeWord(leftSibling, memo)
  const rightPart = getWholeWord(rightSibling, memo)

  return new Set([...memo, ...leftPart, ...rightPart])
}

// Sorts Set of HTML Elements returned by getWholeWord and return word boundaries
export const getWordRange = (el: Element): ElementRange => {
  const wordElements = getWholeWord(el)
  const sortedArray = [...wordElements].sort(
    (a, b) => getSpanNumberFromId(a.id) - getSpanNumberFromId(b.id)
  )
  return {
    first: sortedArray[0],
    last: sortedArray[sortedArray.length - 1],
  }
}

// Get nearest words on to both sides
const getNearestWords = (
  elRange: ElementRange,
  doc: Document
): { prevWord: ElementRange; nextWord: ElementRange } => {
  const elFirstId = getSpanNumberFromId(elRange.first.id)
  const elLastId = getSpanNumberFromId(elRange.last.id)
  let prevEl = doc.querySelector(`#_idTextSpan${elFirstId - 1}`)
  let nextEl = doc.querySelector(`#_idTextSpan${elLastId + 1}`)

  if (!prevEl || !prevEl.textContent?.trim()) {
    prevEl = doc.querySelector(`#_idTextSpan${elFirstId - 2}`)
  }

  if (!nextEl || !nextEl.textContent?.trim()) {
    nextEl = doc.querySelector(`#_idTextSpan${elLastId + 2}`)
  }

  return { prevWord: getWordRange(prevEl), nextWord: getWordRange(nextEl) }
}

// Checks if any of the word is highlighted and returns CFI of the highlight
const wordCfi = (
  wordRange: ElementRange,
  controller: TODO
): string | undefined => {
  const firstCfi = controller.highlightedSpans[wordRange?.first?.id]
  const lastCfi = controller.highlightedSpans[wordRange?.last?.id]

  return firstCfi || lastCfi
}

// Programmatically sets user selection from first to last node
const highlightRange = ({ first, last }: ElementRange, doc: Document) => {
  const range = new Range()
  range.setStart(first.firstChild, 0)
  range.setEnd(last.firstChild, last.firstChild.length)
  const selection = doc?.getSelection()
  if (!selection) return
  selection.removeAllRanges()
  selection.addRange(range)
}

// Finds nearest words and if one of them is highlighted, includes passed word in a highlight
export const mergeHighlights = (elRange: ElementRange, controller: TODO) => {
  const doc = controller.document
  const { prevWord, nextWord } = getNearestWords(elRange, doc)

  const prevHighlightCfi = wordCfi(prevWord, controller)
  const nextHighlightCfi = wordCfi(nextWord, controller)

  let highlightRangeStart = elRange.first
  let highlightRangeEnd = elRange.last

  if (prevHighlightCfi) {
    controller.handleAnnotationRemove(false, prevHighlightCfi)
    highlightRangeStart = doc.querySelector(
      `#_idTextSpan${getSpanIdFromCfi(prevHighlightCfi)[0]}`
    )
  }

  if (nextHighlightCfi) {
    controller.handleAnnotationRemove(false, nextHighlightCfi)
    highlightRangeEnd = doc.querySelector(
      `#_idTextSpan${getSpanIdFromCfi(nextHighlightCfi)[1]}`
    )
  }

  highlightRange({ first: highlightRangeStart, last: highlightRangeEnd }, doc)
}

export const preventUserSelection = () => {
  const { frames } = window
  try {
    for (let i = 1; i < frames.length; i++) {
      if (frames[i].frameElement?.parentNode?.className === 'epub-view') {
        frames[i].document.body.style.userSelect = 'none'
      }
    }
  } catch (e) {
    console.error(e.message)
  }
}
